import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../auth';
import { useLanguage } from '../i18n';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from '../components/ui/dialog';
import { Plus, Tags } from 'lucide-react';
import { toast } from 'sonner';

export default function CategoriesPage() {
  const { t, getCategoryName } = useLanguage();
  const [categories, setCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name_ko: '', name_ru: '', name_en: '', name_uz: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const res = await axios.get(`${API}/product-categories`);
    setCategories(res.data);
  };

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/product-categories`, form);
      toast.success('Category created');
      setDialogOpen(false);
      setForm({ name_ko: '', name_ru: '', name_en: '', name_uz: '' });
      fetchData();
    } catch (err) {
      toast.error('Failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="categories-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">{t('productCategories')}</h1>
          <p className="text-gray-500 mt-1">{categories.length} categories</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="cff-button-primary" data-testid="add-category-btn">
              <Plus className="h-4 w-4 mr-2" /> {t('add')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t('add')} {t('productCategories')}</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label>한국어</Label>
                <Input value={form.name_ko} onChange={(e) => setForm({...form, name_ko: e.target.value})} data-testid="cat-name-ko" />
              </div>
              <div>
                <Label>Русский</Label>
                <Input value={form.name_ru} onChange={(e) => setForm({...form, name_ru: e.target.value})} />
              </div>
              <div>
                <Label>English</Label>
                <Input value={form.name_en} onChange={(e) => setForm({...form, name_en: e.target.value})} />
              </div>
              <div>
                <Label>O'zbekcha</Label>
                <Input value={form.name_uz} onChange={(e) => setForm({...form, name_uz: e.target.value})} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('cancel')}</Button>
              <Button onClick={handleCreate} className="cff-button-primary" data-testid="save-cat-btn">{t('save')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {categories.map(c => (
          <Card key={c.id} className="p-5 cff-hover-lift" data-testid={`cat-card-${c.id}`}>
            <div className="bg-orange-50 p-2 rounded-md w-fit mb-3">
              <Tags className="h-4 w-4 text-[#556B2F]" />
            </div>
            <p className="font-semibold text-gray-900">{getCategoryName(c)}</p>
            <div className="text-xs text-gray-500 mt-2 space-y-0.5">
              <p>KO: {c.name_ko}</p>
              {c.name_en && <p>EN: {c.name_en}</p>}
              {c.name_ru && <p>RU: {c.name_ru}</p>}
              {c.name_uz && <p>UZ: {c.name_uz}</p>}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
